import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import {
  getBagSurveyStatus,
  getBagSurveyStatusByHorizon,
  getUserById,
  setNachversandEligibility,
  updateEntryQuestionnaire,
} from '../request/authenticated-requests/user';
import {
  CheckListId,
  ErrorCode,
  StammdatenQuestionnaireQuestionKey,
} from '@web/common';
import type {
  EntryQuestionnaireReqDtoType,
  OnboardingDataResDto,
  UserBagSurveyHorizonStatusDto,
  UserBmsSampleResDto,
} from '@web/common';
import { BMSSampleDecision } from '@web/common';
import { setupMobX } from '../util/setupMobX';
import { RequestErrorType, handleRequestError } from '../request';
import { showErrorNotification } from '../component/notification';
import {
  getBmsSample,
  getParticipantQrCodeContent,
  setBmsSampleDecision,
  setParticipantQrCodeContent,
} from '../request/authenticated-requests/participant';
import { DateTime } from 'luxon';
import { CohortType } from '@prisma/client';
import { MyCohortStore } from './MyCohortStore';

/**
 * this is an example of a store, that could be closer to the needed place, but then it is always rerendered (if done wrong) and the store is recreated
 * => improvement
 */
export class ParticipantDataStore {
  @observable public data: OnboardingDataResDto | null = null;
  @observable public participantName: string | null = null;
  @observable public isLoading = false;
  @observable public qrCodeContent: string | null = null;
  @observable public bagSurvey: {
    horizon0: UserBagSurveyHorizonStatusDto | null;
    horizon1: UserBagSurveyHorizonStatusDto | null;
  } = { horizon0: null, horizon1: null };
  @observable public bmsSample: UserBmsSampleResDto | null = null;

  @computed public get myCohortStartDate(): DateTime | null {
    return this.myCohortStore.cohortStartDate;
  }
  @computed public get myCohortType(): CohortType | null {
    return this.myCohortStore.cohortType;
  }

  @computed public get isHorizon1Due(): boolean {
    const h1 = this.bagSurvey.horizon1;
    return h1 !== null && DateTime.fromJSDate(h1.startDate) <= DateTime.now();
  }

  @computed public get horizon1BannerLink(): string | null {
    const h1 = this.bagSurvey.horizon1;
    if (h1 === null || h1.filledOut || !this.isHorizon1Due) return null;
    return h1.surveyLink;
  }

  constructor(private readonly myCohortStore: MyCohortStore) {
    makeObservable(this);
  }

  @computed public get stammdaten() {
    return (
      this.data?.checkListData?.data?.[CheckListId.StammdatenQuestionnaire]
        ?.questionnaireResult || null
    );
  }

  @computed public get cannabisConsumptionData() {
    return (
      this.data?.checkListData?.data?.[
        CheckListId.CannabisConsumptionQuestionnaire
      ]?.questionnaireResult || null
    );
  }

  @action public getParticipantName() {
    if (!this.stammdaten) return 'TBD';

    const vorname =
      this.stammdaten[StammdatenQuestionnaireQuestionKey.Name] ?? 'Anonym';
    const nachname =
      this.stammdaten[StammdatenQuestionnaireQuestionKey.Surname] ?? 'Anonym';

    return vorname + ' ' + nachname;
  }

  @action public hasStammdaten() {
    return !!this.stammdaten;
  }

  @action public hasCannabisConsumptionData() {
    return !!this.cannabisConsumptionData;
  }

  @action public getParticipantSex() {
    if (!this.stammdaten) return null;
    return this.stammdaten.sex;
  }

  @action public getStammDaten() {
    return this.stammdaten;
  }

  @action public reset() {
    runInAction(() => {
      this.data = null;
      this.participantName = null;
      this.qrCodeContent = null;
      this.bagSurvey = { horizon0: null, horizon1: null };
      this.bmsSample = null;
      this.isLoading = false;
    });
  }

  @action public reload() {
    runInAction(() => {
      if (!this.data) return;
      this.retrieveUserInformation(this.data.id);
    });
  }

  @action public getParticipantQrCodeContent() {
    getParticipantQrCodeContent()
      .then((response) =>
        runInAction(() => (this.qrCodeContent = response.qrCodeContent)),
      )
      .catch((error) => handleRequestError(error));
  }

  @action public async setParticipantQrCodeContent(
    participantId: string,
    qrCodeContent: string,
  ) {
    try {
      await setParticipantQrCodeContent(participantId, {
        'qr-code-content': qrCodeContent,
      });
    } catch (error) {
      handleRequestError(error);
    }
  }

  @action public retrieveUserInformation(userId: string) {
    if (this.isLoading) return;

    const isDifferentParticipant = this.data?.id !== userId;
    if (isDifferentParticipant) {
      runInAction(() => {
        this.participantName = null;
        this.qrCodeContent = null;
        this.bagSurvey = { horizon0: null, horizon1: null };
        this.bmsSample = null;
      });
    }

    this.isLoading = true;

    getUserById(userId)
      .then((response) => {
        runInAction(() => {
          this.data = response;
          this.isLoading = false;
          this.participantName = this.getParticipantName();
        });
      })
      .catch((error) => {
        handleRequestError(
          error,
          [
            RequestErrorType.ParseIssue,
            () => showErrorNotification(RequestErrorType.ParseIssue),
          ],
          [
            ErrorCode.RecordNotFound,
            () => showErrorNotification(ErrorCode.RecordNotFound),
          ],
          [
            ErrorCode.NoOnboardingAppointmentFound,
            () => showErrorNotification(ErrorCode.NoOnboardingAppointmentFound),
          ],
        );
        runInAction(() => {
          this.data = null;
          this.isLoading = false;
        });
      });
  }

  @action public getBagSurveyStatus() {
    getBagSurveyStatus()
      .then((response) => {
        runInAction(() => {
          this.bagSurvey = {
            horizon0: getBagSurveyStatusByHorizon(response, 0),
            horizon1: getBagSurveyStatusByHorizon(response, 1),
          };
        });
      })
      .catch((error) =>
        handleRequestError(error, [
          ErrorCode.RecordNotFound,
          () => showErrorNotification(ErrorCode.RecordNotFound),
        ]),
      );
  }

  @action public getBmsSample(participantId: string) {
    getBmsSample(participantId)
      .then((response) =>
        runInAction(() => {
          this.bmsSample = response;
        }),
      )
      .catch((error) =>
        handleRequestError(error, [
          ErrorCode.RecordNotFound,
          () => {
            runInAction(() => (this.bmsSample = null));
            showErrorNotification(ErrorCode.RecordNotFound, null, {
              title: 'Keine BMS-Abgabe hinterlegt',
              message: 'Für diesen Teilnehmer liegt noch keine BMS-Probe vor.',
            });
          },
        ]),
      );
  }

  @action public setBmsSampleDecision(
    participantId: string,
    decision: BMSSampleDecision,
  ) {
    setBmsSampleDecision(participantId, decision)
      .then((updated) =>
        runInAction(() => {
          this.bmsSample = updated;
        }),
      )
      .catch((error) => handleRequestError(error));
  }

  @action public async setNachversandEligibility(
    userId: string,
    eligible: boolean,
  ) {
    const updated = await setNachversandEligibility(userId, eligible);
    runInAction(() => {
      // only patch if the edited participant is still the loaded one
      if (this.data?.id === userId && this.data.Participant) {
        this.data.Participant.nachversandEligible = updated.nachversandEligible;
      }
    });
  }

  @action public async updateEntryQuestionnaire(
    userId: string,
    entryQuestionnaire: EntryQuestionnaireReqDtoType,
  ) {
    const updated = await updateEntryQuestionnaire(userId, entryQuestionnaire);
    runInAction(() => {
      // only patch if the edited user is still the loaded one
      if (this.data?.id === userId) {
        this.data.entryQuestionnaire = updated;
      }
    });
  }

  @action public getMyCohort() {
    this.myCohortStore.loadMyCohort();
  }
}

const { provider, useStore } = setupMobX<ParticipantDataStore>();
export const useParticipantDataStore = useStore;
export const ParticipantDataProvider = provider;
